import type { FC, ReactNode } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Divider, useTheme } from '@mui/material'
import type { ClaudeSession, Command, ConversationTurnEntry } from '@/index'
import { resolveEventTypeColorFor } from '@/features/events/shared/types/enums/EventType'
import { isUserOptionsActive, shouldRenderUserOptions } from '@/features/events/shared/types'
import { isUserOptionSelection } from '@/features/events/conversation/state/types'
import { BaseBubble } from './BaseBubble'
import { UserOptionsRenderer } from '../dialogs/UserOptionsRenderer'

interface NotificationBubbleProps {
  readonly event: ConversationTurnEntry
  readonly session: ClaudeSession
  readonly latestEvent: ConversationTurnEntry | null
  readonly allUserCommands: Command[]
  readonly children: ReactNode
  readonly showUserOptions?: boolean
  readonly onOptionSelect?:
    | ((optionIndex: number, event: ConversationTurnEntry) => void)
    | undefined
  readonly showTimestamp?: boolean
  readonly onEventDetailsClick?: ((event: ConversationTurnEntry) => void) | undefined
  readonly customBorderColor?: string
  readonly customSelectionColor?: string
}

export const NotificationBubble: FC<NotificationBubbleProps> = ({
  event,
  session,
  latestEvent,
  allUserCommands,
  children,
  showUserOptions = true,
  onOptionSelect,
  showTimestamp = true,
  onEventDetailsClick,
  customBorderColor,
  customSelectionColor,
}) => {
  const theme = useTheme()
  const shouldShowOptions = showUserOptions && shouldRenderUserOptions(event, session)
  const isActive = shouldShowOptions && isUserOptionsActive(event, latestEvent, allUserCommands)
  const agentDialogue = event.notificationContext?.agentDialogue
  const borderColor = customBorderColor || (isActive ? undefined : theme.palette.text.disabled)
  const selectionColor = customSelectionColor || resolveEventTypeColorFor(event.eventType, theme)

  const { selectedOptionIndex, customPrompt } = useMemo(() => {
    const userCommand = allUserCommands.find(
      (cmd) => isUserOptionSelection(cmd) && cmd.refEventId === event.eventId,
    )
    return {
      selectedOptionIndex: userCommand?.userOption?.index,
      customPrompt: userCommand?.userPrompt ?? null,
    }
  }, [allUserCommands, event.eventId])

  return (
    <BaseBubble
      event={event}
      showTimestamp={showTimestamp}
      onEventDetailsClick={onEventDetailsClick}
      borderColor={borderColor}
    >
      <Box>{children}</Box>

      {shouldShowOptions && agentDialogue && (
        <>
          <Divider sx={dividerSx} />
          <Box>
            <UserOptionsRenderer
              options={agentDialogue.userOptions}
              isActive={isActive}
              onOptionSelect={(index) => onOptionSelect?.(index, event)}
              showTitle={true}
              selectionColor={selectionColor}
              {...(selectedOptionIndex !== undefined && { selectedOptionIndex })}
              {...(customPrompt && { customPrompt })}
            />
          </Box>
        </>
      )}
    </BaseBubble>
  )
}

const dividerSx: SxProps<Theme> = {
  my: 2,
}
